import React from "react";
import { Button, cn } from "@ndd/ui";
import type { RdHoursWeekdayName } from "../../../../../shared/api-types";
import { dayOfWeekLabel, monthDayLabel } from "../../../lib/rd-hours-client";
import type { BlockRef, RdHoursMode } from "../../../lib/rd-hours-draft";
import { lunchRangeLabel } from "../../../lib/rd-hours-draft";
import {
  RD_HOURS_DAYS,
  STEP_MINUTES,
  blockNeedsBreak,
  breakSteps,
  dayLetters,
  dayName,
  dayShortName,
  hoursFigure,
  rangeLabel,
  rangeSteps,
  timeLabel,
  tooLongStretches,
  type HoursBlock,
  type HoursWeek,
} from "../../../lib/rd-hours-editor";
import { Stepper } from "../stepper";
import { CARD, DISPLAY_TITLE, FOCUS, RULE } from "./look";

export interface SidePanelProps {
  mode: RdHoursMode;
  weekStart: string | null;
  nextWeekStart: string;
  days: HoursWeek;
  selection: BlockRef | null;
  thresholdMinutes: number;
  copyTo: readonly RdHoursWeekdayName[];
  /** The RD has never saved hours. */
  fresh: boolean;
  onRange: (start: number, end: number) => void;
  onAddBreak: () => void;
  onMoveBreak: (delta: number) => void;
  onBreakLength: (minutes: 30 | 60) => void;
  onRemoveBreak: () => void;
  onToggleCopy: (day: RdHoursWeekdayName) => void;
  onApplyCopy: () => void;
  onDelete: () => void;
}

export function noSelectionText(fresh: boolean): string {
  return fresh
    ? "Click an hour on any day to add it. Then set a lunch break and copy the day to the rest of your week."
    : "Select a block of hours to change it or its lunch break, or click an empty hour to add one.";
}

export function selectionTitle(
  mode: RdHoursMode,
  day: RdHoursWeekdayName,
  weekStart: string | null
): string {
  return mode === "week" && weekStart !== null
    ? `${dayOfWeekLabel(weekStart, day)} only`
    : `${dayName(day)} hours`;
}

export function selectionScope(
  mode: RdHoursMode,
  day: RdHoursWeekdayName,
  nextWeekStart: string
): string {
  return mode === "week"
    ? `Your usual ${dayName(day)} hours stay the same.`
    : `Every ${dayName(day)}, from ${monthDayLabel(nextWeekStart)}`;
}

export function copyButtonLabel(copyTo: readonly RdHoursWeekdayName[]): string {
  return copyTo.length === 0
    ? "Pick days to copy to"
    : `Copy to ${copyTo.map(dayName).join(", ")}`;
}

function breakHelp(needs: boolean, thresholdMinutes: number): string {
  const hours = hoursFigure(thresholdMinutes);

  return needs
    ? `These hours run more than ${hours} hours straight, so they need a break.`
    : `Under ${hours} hours, so a break is up to you.`;
}

function HoursSteppers({
  props,
  target,
  block,
}: {
  props: SidePanelProps;
  target: BlockRef;
  block: HoursBlock;
}) {
  const steps = rangeSteps(props.days[target.day], target.index);

  return (
    <>
      <Stepper
        name="Starts"
        value={timeLabel(block.start)}
        lessLabel="Start 30 minutes earlier"
        moreLabel="Start 30 minutes later"
        noLess={!steps.startEarlier}
        noMore={!steps.startLater}
        onLess={() => props.onRange(block.start - STEP_MINUTES, block.end)}
        onMore={() => props.onRange(block.start + STEP_MINUTES, block.end)}
      />
      <Stepper
        name="Ends"
        value={timeLabel(block.end)}
        lessLabel="End 30 minutes earlier"
        moreLabel="End 30 minutes later"
        noLess={!steps.endEarlier}
        noMore={!steps.endLater}
        onLess={() => props.onRange(block.start, block.end - STEP_MINUTES)}
        onMore={() => props.onRange(block.start, block.end + STEP_MINUTES)}
      />
    </>
  );
}

/** Why a break that already exists is not enough: the stretch it leaves too long. */
function BreakStillRequired({
  block,
  thresholdMinutes,
}: {
  block: HoursBlock;
  thresholdMinutes: number;
}) {
  const long = tooLongStretches([block], thresholdMinutes);

  if (long.length === 0) return null;

  return (
    <span className="text-body-lg text-muted-strong">
      Still required: {long.map(rangeLabel).join(" and ")} runs more than{" "}
      {hoursFigure(thresholdMinutes)} hours without a break. Move the break or shorten
      the day.
    </span>
  );
}

function BreakTag({ block, needs }: { block: HoursBlock; needs: boolean }) {
  if (block.lunchBreak !== null) {
    return (
      <span className="font-mono text-body text-muted-strong">
        {lunchRangeLabel(block.lunchBreak.start, block.lunchBreak.minutes)}
      </span>
    );
  }

  return needs ? (
    <span className="text-body font-semibold text-danger">Required</span>
  ) : (
    <span className="text-body text-muted-foreground">Optional</span>
  );
}

function BreakEditor({
  props,
  lunch,
  steps,
}: {
  props: SidePanelProps;
  lunch: NonNullable<HoursBlock["lunchBreak"]>;
  steps: ReturnType<typeof breakSteps>;
}) {
  return (
    <>
      <Stepper
        name="Starts"
        value={timeLabel(lunch.start)}
        lessLabel="Move break 30 minutes earlier"
        moreLabel="Move break 30 minutes later"
        noLess={!steps.earlier}
        noMore={!steps.later}
        onLess={() => props.onMoveBreak(-STEP_MINUTES)}
        onMore={() => props.onMoveBreak(STEP_MINUTES)}
      />
      <Stepper
        name="Length"
        value={lunch.minutes === 60 ? "1 hour" : "30 min"}
        lessLabel="Shorter break"
        moreLabel="Longer break"
        noLess={!steps.shorter}
        noMore={!steps.longer}
        onLess={() => props.onBreakLength(30)}
        onMore={() => props.onBreakLength(60)}
      />
      <button
        type="button"
        onClick={props.onRemoveBreak}
        className={cn(
          "self-start text-body font-medium text-muted-foreground underline underline-offset-[3px]",
          FOCUS
        )}
      >
        Remove break
      </button>
    </>
  );
}

function BreakEmpty({ props, needs }: { props: SidePanelProps; needs: boolean }) {
  return (
    <>
      <span className="text-body-lg text-muted-strong">
        {breakHelp(needs, props.thresholdMinutes)}
      </span>
      <Button
        type="button"
        variant="utility"
        className="h-[34px] self-start rounded-md px-3.5 text-body-lg font-semibold"
        onClick={props.onAddBreak}
      >
        Add a lunch break
      </Button>
    </>
  );
}

function BreakSection({ props, block }: { props: SidePanelProps; block: HoursBlock }) {
  const needs = blockNeedsBreak(block, props.thresholdMinutes);
  const lunch = block.lunchBreak;
  const steps = breakSteps(block);

  return (
    <div className={cn("flex flex-col gap-2.5 border-t pt-3.5", RULE)}>
      <span className="flex items-baseline justify-between gap-2">
        <span className="text-body-lg font-semibold">Lunch break</span>
        <BreakTag block={block} needs={needs} />
      </span>
      {lunch !== null ? (
        <>
          <BreakStillRequired block={block} thresholdMinutes={props.thresholdMinutes} />
          <BreakEditor props={props} lunch={lunch} steps={steps} />
        </>
      ) : (
        <BreakEmpty props={props} needs={needs} />
      )}
    </div>
  );
}

/** "Copied to Tue, Wed": shown under the copy button for a few seconds. */
export function copiedLine(days: readonly RdHoursWeekdayName[]): string {
  return `Copied to ${days.map(dayShortName).join(", ")}`;
}

const COPIED_MS = 4000;

/** The copied line: set on Copy, cleared after a few seconds or the next pick. */
function useCopiedLine() {
  const [line, setLine] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (line === null) return;

    const timer = window.setTimeout(() => setLine(null), COPIED_MS);

    return () => window.clearTimeout(timer);
  }, [line]);

  return { line, show: setLine, clear: () => setLine(null) };
}

function CopyDayButton({
  day,
  self,
  on,
  onToggle,
}: {
  day: RdHoursWeekdayName;
  /** The day being copied from, which cannot be a target. */
  self: boolean;
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={dayName(day)}
      disabled={self}
      onClick={onToggle}
      className={cn(
        "h-8 rounded-md text-body-sm font-semibold",
        FOCUS,
        self && "cursor-default border border-transparent bg-muted text-subtle",
        !self &&
          on &&
          "bg-primary-soft text-primary shadow-[inset_0_0_0_1.5px_var(--primary)]",
        !self && !on && "border border-border-strong bg-card text-foreground"
      )}
    >
      {dayLetters(day)}
    </button>
  );
}

function CopySection({
  props,
  source,
}: {
  props: SidePanelProps;
  source: RdHoursWeekdayName;
}) {
  const labelId = React.useId();
  const copied = useCopiedLine();

  return (
    <div className={cn("flex flex-col gap-2 border-t pt-3.5", RULE)}>
      <span id={labelId} className="text-body-lg font-semibold">
        Copy {dayName(source)}&apos;s hours and break to
      </span>
      <div role="group" aria-labelledby={labelId} className="grid grid-cols-7 gap-1">
        {RD_HOURS_DAYS.map((day) => (
          <CopyDayButton
            key={day}
            day={day}
            self={day === source}
            on={props.copyTo.includes(day)}
            onToggle={() => {
              copied.clear();
              props.onToggleCopy(day);
            }}
          />
        ))}
      </div>
      <Button
        type="button"
        variant="utility"
        className="h-9 rounded-md text-body-lg font-semibold disabled:border-transparent disabled:bg-muted disabled:text-subtle disabled:opacity-100"
        disabled={props.copyTo.length === 0}
        onClick={() => {
          copied.show(copiedLine(props.copyTo));
          props.onApplyCopy();
        }}
      >
        {copyButtonLabel(props.copyTo)}
      </Button>
      {copied.line !== null ? (
        <span className="text-body text-muted-foreground">{copied.line}</span>
      ) : null}
    </div>
  );
}

function SelectedBlockCard({
  props,
  selection,
}: {
  props: SidePanelProps;
  selection: BlockRef;
}) {
  const titleId = React.useId();
  const block = props.days[selection.day][selection.index];

  if (block === undefined) return null;

  return (
    <section
      aria-labelledby={titleId}
      className={cn(CARD, "flex flex-col gap-3.5 p-[18px]")}
    >
      <div className="flex flex-col gap-0.5">
        <h3 id={titleId} className={DISPLAY_TITLE}>
          {selectionTitle(props.mode, selection.day, props.weekStart)}
        </h3>
        <span className="text-body-lg text-muted-foreground">
          {selectionScope(props.mode, selection.day, props.nextWeekStart)}
        </span>
      </div>
      <HoursSteppers props={props} target={selection} block={block} />
      <BreakSection props={props} block={block} />
      <CopySection key={selection.day} props={props} source={selection.day} />
      <button
        type="button"
        onClick={props.onDelete}
        className={cn("self-start text-body font-medium text-danger", FOCUS)}
      >
        Delete these hours
      </button>
    </section>
  );
}

export function SidePanelCard(props: SidePanelProps) {
  const selection = props.selection;
  const block =
    selection === null ? undefined : props.days[selection.day][selection.index];

  if (selection === null || block === undefined) {
    return (
      <section className={cn(CARD, "p-[18px] text-muted-strong")}>
        {noSelectionText(props.fresh)}
      </section>
    );
  }

  return <SelectedBlockCard props={props} selection={selection} />;
}
